import React, { useState, useEffect } from "react"
import { useHistory, useLocation } from "react-router-dom"
import moment from "moment"

import {
   ExportTimelineGeneratePdf,
   GetProductionLineDetails,
   GetProductionLinesListing,
   GetTimeLineListing,
} from "api/api.service"

import ProductionScheduleCalendar from "./ProductionScheduleCalendar"
import { ProductionScheduleProvider } from "./ProductionScheduleContext"
import { dateTimeFormat, parseDate } from "utils/dateFormatUtils"
import ProductionScheduleHeader from "./ProductionScheduleHeader"
import useAsyncSelect from "hooks/useAsyncSelect"
import { APP_ROUTES, getRoute } from "helpers/routeHelpers"
import ProductionLineStatus from "constants/ProductionLineStatus"
import { downloadPdf } from "utils/downloadUtils"
import toast from "react-hot-toast"
import ExportDateModal from "../Modals/ExportDateModal/ExportDateModal"
import { PERIODS_TYPES, DATE_PERIODS_TYPES } from "constants/DatePeriods"

const DATA_KEY = "data"
const TOTAL_KEY = "total"
const UNLIMITED_PRODUCTION_LINE_LIMIT = -1

const initialRangeValue = [
   {
      startDate: undefined,
      endDate: undefined,
      key: "selection",
      type: DATE_PERIODS_TYPES.WEEK,
   },
]

const createCurrentWeekDateRange = () => {
   const now = moment()
   const startOfWeek = now.startOf("week").toDate()
   const endOfWeek = now.endOf("week").toDate()

   return [
      {
         startDate: startOfWeek,
         endDate: endOfWeek,
         key: "selection",
         type: DATE_PERIODS_TYPES.WEEK,
      },
   ]
}

const ProductionSchedule = () => {
   const location = useLocation()
   const history = useHistory()
   const { id: timeLineId, productionLineId: productionLineId } =
      (location && location.state) || {}
   const [isLoading, setIsLoading] = useState(false)
   const [productionDetailsData, setProductionDetailsData] = useState({})
   const [productionFilter, setProductionFilter] = useState(null)
   const [range, setRange] = useState(initialRangeValue)
   const [tempRange, setTempRange] = useState(initialRangeValue)
   const [showWarningModal, setShowWarningModal] = useState(false)
   const [isFullScreenView, setIsFullScreenView] = useState(false)
   const [isExporting, setIsExporting] = useState(false)
   const [showExportDateModal, setShowExportDateModal] = useState(false)
   const [selectedPeriods, setSelectedPeriods] = useState([])
   const [combinedTimeLineData, setCombinedTimeLineData] = useState([])
   const [isLoadingWeeks, setIsLoadingWeeks] = useState(false)
   const [weekDataCache, setWeekDataCache] = useState(new Map())
   const [currentMonth, setCurrentMonth] = useState(null)

   useEffect(() => {
      if (productionLineId && !productionFilter) {
         const filterValue = {
            id: productionLineId,
            label: `Production Line ${productionLineId}`,
            value: productionLineId,
            name: `Production Line ${productionLineId}`,
         }
         setProductionFilter(filterValue)

         fetchProductionLineDetails(productionLineId)

         const currentWeekRange = createCurrentWeekDateRange()
         setRange(currentWeekRange)
         setSelectedPeriods(currentWeekRange)

         fetchMultipleWeeksData(currentWeekRange, productionLineId)
      }
   }, [productionLineId, productionFilter])

   const getDateRangeParams = dateRange => {
      if (!dateRange) return {}
      const params = {}
      if (dateRange.startDate) {
         params.dateFrom = moment
            .utc(dateRange.startDate)
            .startOf("day")
            .format(dateTimeFormat.apiDateTimeFormat)
      }
      if (dateRange.endDate) {
         params.dateTo = moment
            .utc(dateRange.endDate)
            .endOf("day")
            .format(dateTimeFormat.apiDateTimeFormat)
      }
      return params
   }

   const fetchWeekData = async (weekPeriod, productionLineId) => {
      const weekKey = `${weekPeriod.startDate.toISOString()}_${weekPeriod.endDate.toISOString()}`

      if (weekDataCache.has(weekKey)) {
         return weekDataCache.get(weekKey)
      }

      const params = {
         limit: -1,
         productionLineId: productionLineId,
         ...getDateRangeParams(weekPeriod),
      }

      try {
         const res = await GetTimeLineListing(params)
         const weekData = res?.data || []

         setWeekDataCache(prev => new Map(prev.set(weekKey, weekData)))
         return weekData
      } catch (error) {
         console.error(`Error fetching data for week ${weekKey}:`, error)
         return []
      }
   }

   const fetchMultipleWeeksData = async (periods, productionLineId) => {
      if (!periods || periods.length === 0) {
         setCombinedTimeLineData([])
         return
      }

      setIsLoadingWeeks(true)

      try {
         const allWeekData = []

         for (const period of periods) {
            const weekData = await fetchWeekData(period, productionLineId)
            allWeekData.push(...weekData)
         }

         const sortedData = allWeekData.sort(
            (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()
         )

         setCombinedTimeLineData(sortedData)
      } catch (error) {
         console.error("Error fetching multiple weeks data:", error)
         setCombinedTimeLineData([])
      } finally {
         setIsLoadingWeeks(false)
      }
   }

   const hasMonthChanged = newPeriods => {
      if (!currentMonth || !newPeriods || newPeriods.length === 0) return true

      const newMonth = moment(newPeriods[0].startDate).format("YYYY-MM")
      const currentMonthStr = moment(currentMonth).format("YYYY-MM")

      return newMonth !== currentMonthStr
   }

   const getCombinedDateRange = () => {
      if (!selectedPeriods || selectedPeriods.length === 0) {
         if (!range[0]?.startDate) {
            const currentWeekRange = createCurrentWeekDateRange()
            return currentWeekRange
         }
         return [range[0]]
      }

      if (selectedPeriods.length === 1) {
         return [selectedPeriods[0]]
      }

      const allDates = selectedPeriods.flatMap(period => [
         moment(period.startDate),
         moment(period.endDate),
      ])

      const minDate = moment.min(allDates)
      const maxDate = moment.max(allDates)

      const combinedRange = {
         startDate: minDate.toDate(),
         endDate: maxDate.toDate(),
         key: "selection",
         type: PERIODS_TYPES.COMBINED,
      }

      return [combinedRange]
   }

   const timeLineData = selectedPeriods.length >= 1 ? combinedTimeLineData : []
   const isFetching = isLoadingWeeks

   const fetchProductionLineDetails = async id => {
      setIsLoading(true)
      GetProductionLineDetails(id)
         .then(detailsRes => {
            if (detailsRes) {
               if (!productionFilter) {
                  const filterValue = {
                     id: detailsRes?.id,
                     label: detailsRes?.name,
                     value: detailsRes?.id,
                     name: detailsRes?.name,
                  }
                  setProductionFilter(filterValue)
               }
               setProductionDetailsData(detailsRes)
            }
         })
         .catch(err => {
            console.error(err)
         })
         .finally(() => {
            setTimeout(() => setIsLoading(false), 500)
         })
   }

   const {
      selectEntity: productionListing,
      handleSearchOnOptions,
      handleSelectAOption,
   } = useAsyncSelect({
      apiFunc: GetProductionLinesListing,
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
      labelKey: "name",
      valueKey: "id",
      deps: [],
      params: {
         status: ProductionLineStatus.Active,
         limit: UNLIMITED_PRODUCTION_LINE_LIMIT,
      },
      attachSignal: true,
      shouldAppend: false,
   })

   const handleSetProduction = async option => {
      if (typeof option !== "object" || !option) return
      if (option.value === productionFilter?.value) return

      setProductionFilter(option)
      setShowWarningModal(false)
      setRange(initialRangeValue)
      setSelectedPeriods([])
      setCombinedTimeLineData([])
      setWeekDataCache(new Map())
      setCurrentMonth(null)
      handleSelectAOption(option, option.id)

      await fetchProductionLineDetails(option.id)
   }

   const handleSearch = (value = "") => {
      handleSearchOnOptions(value)
   }

   const handleDateChange = async ({ selections }) => {
      if (selections) {
         if (hasMonthChanged(selections)) {
            setCombinedTimeLineData([])
            setWeekDataCache(new Map())

            if (selections.length > 0) {
               setCurrentMonth(selections[0].startDate)
            }
         }

         setSelectedPeriods(selections)

         if (selections.length > 0) {
            setRange(selections)

            const currentProductionLineId =
               productionFilter?.id ?? productionLineId
            if (currentProductionLineId) {
               await fetchMultipleWeeksData(selections, currentProductionLineId)
            }
         } else {
            setRange(initialRangeValue)
            setCombinedTimeLineData([])
            setCurrentMonth(null)
         }
      }
   }

   const navigateToDetails = () => {
      history.push(
         getRoute(APP_ROUTES.PRODUCTION_LINE_MANAGEMENT_DETAILS, {
            productionLineId: productionFilter?.id ?? productionLineId,
         })
      )
   }

   const handleExport = async selectedDate => {
      const currentProductionLineId = productionFilter?.id ?? productionLineId
      const startDate = selectedDate
         ? moment(selectedDate)
              .startOf("day")
              .format(dateTimeFormat.apiDateTimeFormat)
         : moment().startOf("day").format(dateTimeFormat.apiDateTimeFormat)

      if (!currentProductionLineId) {
         toast.error("Please select a production line")
         return
      }

      try {
         setIsExporting(true)
         const params = {
            productionLineId: currentProductionLineId,
            startDate: startDate,
         }

         const response = await ExportTimelineGeneratePdf(params)
         await downloadPdf(response)
         toast.success("Timeline PDF exported successfully!")
      } catch (error) {
         console.error("Error exporting timeline PDF:", error)
         toast.error("Failed to export timeline PDF. Please try again.")
      } finally {
         setIsExporting(false)
      }
   }

   const handleExportClick = () => {
      setShowExportDateModal(true)
   }

   const handleExportDateConfirm = selectedDate => {
      handleExport(selectedDate)
   }

   const toggleFullscreen = () => {
      const className = "app-fullscreen"
      if (isFullScreenView) {
         document.body.classList.remove(className)
         setIsFullScreenView(false)
      } else {
         document.body.classList.add(className)
         setIsFullScreenView(true)
      }
   }

   return (
      <ProductionScheduleProvider
         value={{
            timeLineId,
            timeLineData,
            overrides: productionDetailsData?.overrides,
            dailyConfigurations: productionDetailsData?.dailyConfigurations,
            stages: productionDetailsData?.stages,
         }}
      >
         <ProductionScheduleHeader
            isLoading={isLoading}
            range={range}
            tempRange={tempRange}
            productionFilter={productionFilter}
            productionListing={productionListing}
            onDateChange={handleDateChange}
            onSetProduction={handleSetProduction}
            onSearch={handleSearch}
            onShowNextInventory={() => setShowWarningModal(prev => !prev)}
            onShowFullScreen={toggleFullscreen}
            isDatePickerDisabled={!productionFilter?.id && !productionLineId}
            productionDetailsData={productionDetailsData}
            navigateToDetails={navigateToDetails}
            onExport={handleExportClick}
            isExporting={isExporting}
            selectedPeriods={selectedPeriods}
            productionLineId={productionFilter?.id || productionLineId}
         />
         <ProductionScheduleCalendar
            isLoading={isLoading || isFetching}
            timeLineData={timeLineData}
            dateRange={getCombinedDateRange()}
            productionDetailsData={productionDetailsData}
            showWarningModal={showWarningModal}
            setShowWarningModal={setShowWarningModal}
            productionLineId={productionFilter?.id ?? productionLineId}
         />

         <ExportDateModal
            isOpen={showExportDateModal}
            onClose={() => setShowExportDateModal(false)}
            onExport={handleExportDateConfirm}
            selectedDate={new Date()}
            minDate={parseDate(productionDetailsData?.startDate)}
         />
      </ProductionScheduleProvider>
   )
}

export default ProductionSchedule
